import { OnInit } from '@angular/core';
import { Component } from '@angular/core';
import { MenuItem } from 'primeng/api';
import { CurrentUserService } from '@core/services/currentUser.service';
import { MenuService } from '../service/app.menu.service';
import { PermissionType } from '@admin/modules/users/models/user.model';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'app-menu',
  templateUrl: './app.menu.component.html',
})
export class AppMenuComponent implements OnInit {
  constructor(
    private currentUser: CurrentUserService,
    private menuService: MenuService,
    private translate: TranslateService,
  ) {}
  model: MenuItem[] = [];

  ngOnInit() {
    this.model = [
      {
        items: [
          {
            label: this.translate.instant('MENU.PANEL'),
            icon: 'layout-grid',
            routerLink: ['/'],
            queryParamsHandling: 'preserve',
          },
          {
            label: this.translate.instant('MENU.CUSTOMER_JOURNEY'),
            icon: 'map',
            routerLink: ['/customer-journey'],
            queryParamsHandling: 'preserve',
          },
          {
            label: this.translate.instant('MENU.BEHAVIORAL_PILL'),
            icon: 'zap',
            routerLink: ['/behavioral-pill'],
            queryParamsHandling: 'preserve',
          },
        ],
      },
      {
        label: this.translate.instant('MENU.OPERATIVA'),
        items: [
          {
            label: this.translate.instant('MENU.CLIENTS'),
            icon: 'users',
            routerLink: ['/customers'],
            queryParamsHandling: 'preserve',
          },
          {
            label: this.translate.instant('MENU.INSTANCES'),
            icon: 'folder',
            routerLink: ['/instances'],
            queryParamsHandling: 'preserve',
          },
          {
            label: this.translate.instant('MENU.OPERATIONS'),
            icon: 'file-text',
            routerLink: ['/operations'],
            queryParamsHandling: 'preserve',
          },
          {
            label: this.translate.instant('DATA.MODULES.PAYMENTS.PAYMENTS'),
            icon: 'credit-card',
            routerLink: ['/payments'],
            queryParamsHandling: 'preserve',
          },
          {
            label: this.translate.instant('MENU.OUTBOUNDCALL'),
            icon: 'phone',
            routerLink: ['/outbound-calls'],
            queryParamsHandling: 'preserve',
          },
          {
            label: 'WhatsApp',
            iconUrl: 'assets/icons/whatsapp.svg',
            routerLink: ['/chat-conversations'],
            queryParamsHandling: 'preserve',
          },
          ...(this.isAllow([PermissionType.FULL_ACCESS, PermissionType.OPERATION_WRITE])
            ? [
                {
                  label: this.translate.instant('MENU.PAYMENT_AGREEMENTS'),
                  icon: 'banknote',
                  routerLink: ['/payment-agreement'],
                  queryParamsHandling: 'preserve' as 'preserve',
                },
              ]
            : []),
        ],
      },
      {
        id: 'campaignsAdmin',
        label: this.translate.instant('MENU.CAMPAIGNS_MANAGER'),
        items: [
          {
            label: this.translate.instant('MENU.CAMPAIGNS'),
            icon: 'network',
            routerLink: ['/campaigns_new'],
            queryParamsHandling: 'preserve',
          },
          {
            label: this.translate.instant('MENU.PORTFOLIOS'),
            icon: 'folder-open',
            routerLink: ['/portfolios'],
            queryParamsHandling: 'preserve',
          },
          {
            label: this.translate.instant('MENU.TEMPLATES'),
            icon: 'message-square',
            routerLink: ['/copy/'],
            queryParamsHandling: 'preserve',
          },
          {
            label: this.translate.instant('MENU.SEGMENTS'),
            icon: 'pie-chart',
            routerLink: ['/segments'],
            queryParamsHandling: 'preserve',
          },
        ],
      },
      {
        separator: true,
      },
      {
        id: 'admin',
        label: this.translate.instant('MENU.ADMINISTRATION'),
        queryParamsHandling: 'preserve',
        items: [
          {
            id: 'admin',
            label: this.translate.instant('MENU.CONFIGURATION'),
            icon: 'settings',
            queryParamsHandling: 'preserve',
            items: [
              {
                label: this.translate.instant('MENU.COLLECTOR_PROFILES'),
                icon: 'box',
                routerLink: ['/collectors'],
                queryParamsHandling: 'preserve',
              },
              {
                label: this.translate.instant('MENU.BRANDS'),
                icon: 'tag',
                routerLink: ['/brands'],
                queryParamsHandling: 'preserve',
              },
              {
                label: this.translate.instant('MENU.NOTIFICATION_CHANNELS'),
                icon: 'send',
                routerLink: ['/channel-configs'],
                queryParamsHandling: 'preserve',
              },
              {
                label: this.translate.instant('MENU.FEEDBACK_OPTIONS'),
                icon: 'arrow-right-left',
                routerLink: ['/feedback-configs'],
                queryParamsHandling: 'preserve',
              },
              {
                label: this.translate.instant('MENU.PAYMENT_METHODS'),
                icon: 'banknote',
                routerLink: ['/payment-methods'],
                queryParamsHandling: 'preserve',
              },
              {
                label: this.translate.instant('MENU.USERS'),
                icon: 'user-plus',
                routerLink: ['/users'],
                queryParamsHandling: 'preserve',
              },
              {
                label: this.translate.instant('MENU.TAX_ENTITIES'),
                icon: 'percent',
                routerLink: ['/tax-entity'],
                queryParamsHandling: 'preserve',
              },
              {
                label: this.translate.instant('MENU.SERVICES'),
                icon: 'briefcase',
                routerLink: ['/services'],
                queryParamsHandling: 'preserve',
              },
              {
                label: this.translate.instant('MENU.REGIONAL_CONFIGURATION'),
                icon: 'map-pin',
                routerLink: ['/regional-configs'],
                queryParamsHandling: 'preserve',
              },
              {
                label: this.translate.instant('MENU.DATA_SHARING'),
                icon: 'trending-up',
                routerLink: ['/event-hooks'],
                queryParamsHandling: 'preserve',
              },
              {
                label: this.translate.instant('MENU.PHASES'),
                icon: 'code',
                routerLink: ['/phases'],
                queryParamsHandling: 'preserve',
              },
            ],
          },
          {
            separator: true,
          },
        ],
      },
    ];

    this.allowedFullItems();
    this.allowAdminItems();
  }

  isAllow(permissions: PermissionType[]): boolean {
    return this.currentUser.allowAccessPermission([...permissions]);
  }

  allowAdminItems() {
    const allowedPermissions = [
      PermissionType.FULL_ACCESS,
      PermissionType.ADMINISTRATION,
      PermissionType.COPY_READ,
    ];
    let restrictedItemIds = ['Plantillas', 'Gestor de campañas'];

    if (!this.isAllow(allowedPermissions)) {
      this.model = this.menuService.removeMenuItems(this.model, restrictedItemIds);
      return;
    }
  }

  allowedFullItems() {
    const allowedPermissions = [PermissionType.FULL_ACCESS];
    let restrictedItemIds = ['Configuración', 'Customer Journey'];

    if (!this.isAllow(allowedPermissions)) {
      this.model = this.menuService.removeMenuItems(this.model, restrictedItemIds);
      return;
    }
  }
}
